import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    Grid,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    TablePagination,
    TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import Shopheader from '../Shop/Shopheader';
import Mashalheader from '../Mashal/Mashalheader';
import Wholesalerheader from '../Wholesale/Wholesaleheader';

export default function ViewPurchase() {
    const { category } = useParams();
    const navigate = useNavigate();

    const [purchases, setPurchases] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // ---------------- FETCH VENDORS ----------------
    const fetchVendors = async () => {
        try {
            const snap = await getDocs(collection(db, 'vendors'));
            const vendorData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setVendors(vendorData);
        } catch (error) {
            toast.error(error.message);
        }
    };

    // ---------------- FETCH PURCHASES ----------------
    const fetchPurchases = () => {
        try {
            const q = query(collection(db, 'purchases'));
            onSnapshot(q, (snap) => {
                const data = snap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setPurchases(data);
            });
        } catch (error) {
            toast.error(error.message);
        }
    };

    useEffect(() => {
        fetchVendors();
        fetchPurchases();
    }, [category]);

    const handleDelete = async (purchaseId) => {
        if (!window.confirm('Are you sure you want to delete this purchase?')) return;

        try {
            // 1. Delete related purchase items
            const itemsSnap = await getDocs(
                query(collection(db, 'purchasesitems'), where('purchaseid', '==', purchaseId))
            );
            for (const docItem of itemsSnap.docs) {
                await deleteDoc(doc(db, 'purchasesitems', docItem.id));
            }

            // 2. Delete related vendor account entries
            const accountsSnap = await getDocs(
                query(collection(db, 'vendoraccounts'), where('purchaseid', '==', purchaseId))
            );
            let dealerId = null;
            for (const docAcc of accountsSnap.docs) {
                dealerId = docAcc.data().dealerId; // save dealerId to update totals
                await deleteDoc(doc(db, 'vendoraccounts', docAcc.id));
            }

            // 3. Delete related vendor payment history (if you want to delete payments for this purchase)
            if (dealerId) {
                const paymentsSnap = await getDocs(
                    query(collection(db, 'vendorpaymenthistory'), where('dealerId', '==', dealerId))
                );
                for (const docPay of paymentsSnap.docs) {
                    if (docPay.data().purchaseId === purchaseId) {
                        await deleteDoc(doc(db, 'vendorpaymenthistory', docPay.id));
                    }
                }
            }

            // 4. Delete purchase document
            await deleteDoc(doc(db, 'purchases', purchaseId));

            // 5. Update vendor totals
            if (dealerId) {
                const purchasesSnap = await getDocs(
                    query(collection(db, 'purchases'), where('dealerId', '==', dealerId))
                );
                const totalPurchase = purchasesSnap.docs.reduce((acc, doc) => acc + parseFloat(doc.data().grandTotal || 0), 0);

                const paymentsSnap = await getDocs(
                    query(collection(db, 'vendorpaymenthistory'), where('dealerId', '==', dealerId))
                );
                const totalPaid = paymentsSnap.docs.reduce((acc, doc) => acc + parseFloat(doc.data().amount || 0), 0);

                const remaining = totalPurchase - totalPaid;

                // Update vendoraccounts collection if needed
                const vendorAccountsSnap = await getDocs(
                    query(collection(db, 'vendoraccounts'), where('dealerId', '==', dealerId))
                );
                for (const docAcc of vendorAccountsSnap.docs) {
                    await updateDoc(doc(db, 'vendoraccounts', docAcc.id), {
                        grandTotal: totalPurchase,
                        paidTotal: totalPaid,
                        remainingTotal: remaining,
                    });
                }
            }

            toast.success('Purchase and related data deleted successfully');
        } catch (error) {
            toast.error(error.message);
        }
    };



    const handleEdit = (purchaseId) => {
        navigate(`/edit-purchase/${category}/${purchaseId}`);
    };

    // ---------------- TABLE PAGINATION ----------------
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // ---------------- FILTERED DATA ----------------
    const filteredPurchases = purchases.filter((p) => {
        const vendorName = vendors.find((v) => v.id === p.dealerId)?.name || '';
        return vendorName.toLowerCase().includes(search.toLowerCase());
    });

    const displayedPurchases = filteredPurchases.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box p={2} mt={8}>
            {category === "shop" && <Shopheader />}
            {category === "mashal" && <Mashalheader />}
            {category === "wholesaler" && <Wholesalerheader />}

            <Typography variant="h5" fontWeight="bold" mb={2}>
                खरेदी बघा  ({category})
            </Typography>

            <Card sx={{ p: 2, mb: 2 }}>
                <TextField
                    label="Search by Vendor"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                />
            </Card>

            <Card sx={{ p: 2, overflowX:"auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {/* <TableCell>Purchase ID</TableCell> */}
                            <TableCell>Action</TableCell>
                            <TableCell>विक्रेता </TableCell>
                            <TableCell>दिनांक </TableCell>
                            <TableCell>ग्रँड टोटल </TableCell>
                            <TableCell>जमा </TableCell>
                            <TableCell>बाकी </TableCell>
                            <TableCell>पेमेंट टाईप </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayedPurchases.map((p) => {
                            const vendorName = vendors.find((v) => v.id === p.dealerId)?.name || p.dealerId;
                            return (
                                <TableRow key={p.id}>
                                    {/* <TableCell>{p.id}</TableCell> */}
                                    <TableCell>
                                        <IconButton color="primary" onClick={() => handleEdit(p.id)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(p.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>{vendorName}</TableCell>
                                    <TableCell>{p.purchaseDate}</TableCell>
                                    <TableCell>{p.grandTotal}</TableCell>
                                    <TableCell>{p.paidAmount}</TableCell>
                                    <TableCell>{p.remaining}</TableCell>
                                    <TableCell>{p.paytype}</TableCell>

                                </TableRow>
                            );
                        })}
                        {filteredPurchases.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No purchases found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={filteredPurchases.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Card>
        </Box>
    );
}
